/**
 * Default configurations for reminder campaigns
 */

// Default channel configurations
const defaultChannelConfig = {
  email: {
    content: '',
    subject: '',
    from_endpoint: '',
    from_name: '',
    reply_to: '',
    priority: 'normal',
    track_opens: true,
    track_clicks: true,
    attachments: []
  },
  sms: {
    content: '',
    from_endpoint: '',
    priority: 'normal',
    track_delivery: true,
    track_replies: true,
    media_urls: []
  },
  voice: {
    content: '',
    from_endpoint: '',
    platform: 'bland_ai',
    voice_id: 'alexa',
    voice_name: 'Alexa (Female)',
    temperature: 0.7,
    max_duration: 5,
    voicemail_message: '',
    voicemail_action: 'hangup',
    record_call: false,
    call_timeout: 60,
    platform_config: {
      // Bland AI specific fields
      task: '',
      pathway_id: '',
      pathway_version: 1,
      first_sentence: '',
      background_track: '',
      model: 'base',
      wait_for_greeting: false,
      block_interruptions: false,
      interruption_threshold: 100,
      sensitive_voicemail_detection: false,
      transfer_phone_number: '',
      transfer_list: {},
      language: 'en-US',
      voicemail_sms: {},
      dispatch_hours: {},
      ignore_button_press: false,
      timezone: 'America/New_York',
      request_data: {},
      tools: [],
      start_time: '',
      voicemail_action: {},
      retry: { wait: 300, voicemail_action: 'leave_message' },
      max_duration: 5,
      record: false,
      webhook: '',
      webhook_events: [],
      metadata: {},
      citation_schema_id: '',
      
      available_tags: [],
      
      precall_dtmf_sequence: '',
      dynamic_data: [],
      keywords: [],
      pronunciation_guide: [],
      local_dialing: false
    },
    webhook_config: {
      webhook: '',
      webhook_events: []
    },
    metadata: {}
  },
  chat: {
    content: '',
    from_endpoint: '',
    platform: 'whatsapp',
    priority: 'normal',
    track_delivery: true,
    track_read: true,
    media_urls: [],
    quick_replies: []
  }
};

/**
 * Creates a default message configuration for a specific channel
 * @param {string} channel - The communication channel (email, sms, voice, chat)
 * @returns {Object} Default message configuration
 */
const createDefaultMessage = (channel) => ({
  template: '',
  content: '',
  email_config: { ...defaultChannelConfig.email },
  sms_config: { ...defaultChannelConfig.sms },
  voice_config: { ...defaultChannelConfig.voice },
  chat_config: { ...defaultChannelConfig.chat }
});

/**
 * Creates a default reminder time configuration
 * @param {string} channel - The communication channel
 * @param {boolean} useRelativeSchedule - Whether to use relative schedule
 * @returns {Object} Default reminder time configuration
 */
const createDefaultReminderTime = (channel, useRelativeSchedule = false) => {
  
  if (useRelativeSchedule) {
    return {
      days_before_relative: 0,
      hours_before: 0,
      minutes_before: 0,
      days_before: null,
      time: null,
      messages: [createDefaultMessage(channel)]
    };
  }
  
  return {
    days_before: 1,
    time: '09:00',
    days_before_relative: null,
    hours_before: null,
    minutes_before: null,
    messages: [createDefaultMessage(channel)]
  };
};

/**
 * Creates a default reminder schedule configuration
 * @param {string} channel - The communication channel
 * @returns {Object} Default reminder schedule configuration
 */
const createDefaultReminderSchedule = () => ({
  business_hours_only: true,
  use_relative_schedule: false,
  timezone: 'UTC',
  reminder_times: [createDefaultReminderTime()]
});

/**
 * Creates a default campaign configuration
 * @param {string} channel - The communication channel
 * @returns {Object} Default campaign configuration
 */
const createDefaultCampaign = () => ({
  name: '',
  description: '',
  account: '',
  crm_campaign: '',
  start_date: '',
  end_date: '',
  active: true,
  is_ongoing: false,
  campaign_type: 'reminder',
  channel: 'email',
  template: '',
  content: '',
  status: 'draft',
  auto_enroll_new_leads: false,
  auto_enroll_filters: [],
  reminder_schedule: createDefaultReminderSchedule(),
  lead_filters: [],
  enable_opt_out: false,
  initial_opt_out_notice: '',
  opt_out_message: ''
});

/**
 * Creates a default channel configuration object
 * @param {string} channel - The communication channel
 * @returns {Object} Default channel configuration
 */
export const createDefaultChannelConfig = (channel) => ({
  ...defaultChannelConfig[channel],
  from_endpoint: '',
  from_endpoint_value: ''
});

/**
 * Validates and normalizes a reminder campaign configuration
 * @param {Object} campaign - The campaign configuration to normalize
 * @returns {Object} Normalized campaign configuration
 */
const normalizeCampaignConfig = (campaign) => {
  
  // Ensure campaign has the required structure
  const normalizedCampaign = {
    channel: campaign.channel || 'email',
    use_relative_schedule: campaign.reminder_schedule?.use_relative_schedule || false,
    business_hours_only: campaign.reminder_schedule?.business_hours_only || false,
    timezone: campaign.reminder_schedule?.timezone || 'UTC',
    reminder_times: []
  };

  // Normalize reminder times
  if (Array.isArray(campaign.reminder_schedule?.reminder_times)) {
    normalizedCampaign.reminder_times = campaign.reminder_schedule.reminder_times.map(time => {
      const normalizedTime = {
        days_before: normalizedCampaign.use_relative_schedule ? null : (time.days_before || 1),
        time: normalizedCampaign.use_relative_schedule ? null : (time.time || '09:00'),
        days_before_relative: normalizedCampaign.use_relative_schedule ? (time.days_before_relative || 0) : null,
        hours_before: normalizedCampaign.use_relative_schedule ? (time.hours_before || 0) : null,
        minutes_before: normalizedCampaign.use_relative_schedule ? (time.minutes_before || 0) : null,
        messages: []
      };

      // Normalize messages
      if (Array.isArray(time.messages)) {
        normalizedTime.messages = time.messages.map(message => ({
          template: message.template || '',
          content: message.content || '',
          email_config: { ...defaultChannelConfig.email, ...message.email_config },
          sms_config: { ...defaultChannelConfig.sms, ...message.sms_config },
          voice_config: { ...defaultChannelConfig.voice, ...message.voice_config },
          chat_config: { ...defaultChannelConfig.chat, ...message.chat_config }
        }));
      } else {
        // If no messages array exists, create one with a default message
        normalizedTime.messages = [createDefaultMessage(normalizedCampaign.channel)];
      }

      return normalizedTime;
    });
  } else {
    // If no reminder times exist, create one with default values
    normalizedCampaign.reminder_times = [createDefaultReminderTime(normalizedCampaign.channel, normalizedCampaign.use_relative_schedule)];
  }

  return normalizedCampaign;
};

export {
  defaultChannelConfig,
  createDefaultMessage,
  createDefaultReminderTime,
  createDefaultReminderSchedule,
  createDefaultCampaign,
  normalizeCampaignConfig
}; 